import React, { createContext, useCallback, useContext, useRef, useState } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { AlertTriangle, Trash2, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from './Button'

// ─── Types ────────────────────────────────────────────────────────────────────
export type ConfirmVariant = 'danger' | 'warning'

export interface ConfirmConfig {
  title:        string
  description?: string
  confirmText?: string
  cancelText?:  string
  variant?:     ConfirmVariant
}

// ─── Context ──────────────────────────────────────────────────────────────────
interface ConfirmContextValue {
  confirm: (config: ConfirmConfig) => Promise<boolean>
}

const ConfirmContext = createContext<ConfirmContextValue | null>(null)

// ─── Hook ─────────────────────────────────────────────────────────────────────
export function useConfirmDialog(): ConfirmContextValue {
  const ctx = useContext(ConfirmContext)
  if (!ctx) {
    throw new Error('useConfirmDialog must be used within a <ConfirmDialogProvider>')
  }
  return ctx
}

// ─── Provider ─────────────────────────────────────────────────────────────────
interface ProviderProps {
  children: React.ReactNode
}

export const ConfirmDialogProvider: React.FC<ProviderProps> = ({ children }) => {
  const [open, setOpen]       = useState(false)
  const [config, setConfig]   = useState<ConfirmConfig>({
    title:       'Emin misiniz?',
    description: 'Bu işlem geri alınamaz.',
    confirmText: 'Evet',
    cancelText:  'İptal',
    variant:     'danger',
  })
  const resolveRef = useRef<((value: boolean) => void) | null>(null)

  const confirm = useCallback((cfg: ConfirmConfig): Promise<boolean> => {
    setConfig({
      confirmText: 'Evet',
      cancelText:  'İptal',
      variant:     'danger',
      ...cfg,
    })
    setOpen(true)

    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve
    })
  }, [])

  const handleConfirm = () => {
    setOpen(false)
    resolveRef.current?.(true)
    resolveRef.current = null
  }

  const handleCancel = () => {
    setOpen(false)
    resolveRef.current?.(false)
    resolveRef.current = null
  }

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      <ConfirmDialog
        open={open}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        {...config}
      />
    </ConfirmContext.Provider>
  )
}

// ─── ConfirmDialog component (can also be used standalone) ────────────────────
interface ConfirmDialogProps extends ConfirmConfig {
  open:       boolean
  onConfirm:  () => void
  onCancel:   () => void
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  onConfirm,
  onCancel,
  title,
  description,
  confirmText = 'Evet',
  cancelText  = 'İptal',
  variant     = 'danger',
}) => {
  const isDanger = variant === 'danger'

  return (
    <Dialog.Root open={open} onOpenChange={(v) => { if (!v) onCancel() }}>
      <Dialog.Portal>
        <Dialog.Overlay
          className="fixed inset-0 z-modal bg-black/50 backdrop-blur-sm data-[state=open]:animate-fade-in"
        />
        <Dialog.Content
          data-testid="confirm-dialog"
          className={cn(
            'fixed left-1/2 top-1/2 z-modal -translate-x-1/2 -translate-y-1/2',
            'w-full max-w-md',
            'rounded-2xl border border-slate-200 dark:border-slate-700',
            'bg-white dark:bg-slate-900 shadow-card-lg p-6',
            'focus:outline-none',
            'data-[state=open]:animate-modal-in',
          )}
        >
          {/* Close button */}
          <Dialog.Close asChild>
            <button
              onClick={onCancel}
              aria-label="Kapat"
              className={cn(
                'absolute top-4 right-4 p-1.5 rounded-lg',
                'text-slate-400 hover:text-slate-600 dark:hover:text-slate-200',
                'hover:bg-slate-100 dark:hover:bg-slate-700',
                'transition-colors duration-150',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
              )}
            >
              <X className="h-4 w-4" />
            </button>
          </Dialog.Close>

          {/* Icon */}
          <div
            className={cn(
              'mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full',
              isDanger
                ? 'bg-red-100 dark:bg-red-900/20'
                : 'bg-yellow-100 dark:bg-yellow-900/20',
            )}
          >
            {isDanger
              ? <Trash2 className="h-7 w-7 text-red-600 dark:text-red-400" />
              : <AlertTriangle className="h-7 w-7 text-yellow-600 dark:text-yellow-400" />
            }
          </div>

          {/* Text */}
          <div className="text-center mb-6">
            <Dialog.Title className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              {title}
            </Dialog.Title>
            {description && (
              <Dialog.Description className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                {description}
              </Dialog.Description>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              className="flex-1"
              onClick={onCancel}
              data-testid="confirm-cancel"
            >
              {cancelText}
            </Button>
            <Button
              variant={isDanger ? 'danger' : 'primary'}
              className="flex-1"
              onClick={onConfirm}
              data-testid="confirm-ok"
            >
              {confirmText}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
